import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUrlParams } from '../apiService/timespam';
import { API_BASE_URL } from '../apiService/apiClient';
import './WomenLogs.css';

const BASE_URL = `${API_BASE_URL}/goqiiapihealthactivity`;

const WomenLogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const stateData = location.state || {};
  const getTodayFormatted = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const date = stateData.date || getTodayFormatted();
  const selectedDate = stateData.selectedDate || new Date().getDate();
  const currentDate = stateData.currentDate || new Date();
  const USER_ID = stateData.goqiiUserId || '';

  const { lang } = getUrlParams();

  const tl = (ns, name) => {
    const key = `womenLogs.${ns}.${name}`;
    const result = t(key);
    return (result === key ? name : result).replace(/\//g, ' / ');
  };

  const [apiData, setApiData] = useState(null);
  const [flowIntensity, setFlowIntensity] = useState(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/fetch_predictions?goqiiUserId=${USER_ID}&predictionDate=${date}`,
          { headers: { lang } }
        );
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        const result = await response.json();
        if (result?.code === 200 && result?.data) {
          setApiData(result.data);
          if (result.data.loggedData) {
            setFlowIntensity(result.data.loggedData.flowIntensity?.toLowerCase() || null);
            setSelectedMoods(result.data.loggedData.moods || []);
            setSelectedSymptoms(result.data.loggedData.symptoms || []);
          }
        }
      } catch (err) {
        console.error('API error:', err);
        setError('Failed to load options. Please try again.');
      }
    };
    if (USER_ID) fetchData();
  }, [date, USER_ID]);

  const handleFlowIntensityClick = (name) => setFlowIntensity(name);
  const handleSymptomClick = (name) => setSelectedSymptoms(prev =>
    prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]
  );
  const handleMoodClick = (name) => setSelectedMoods(prev =>
    prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]
  );

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    const formattedFlow = flowIntensity
      ? flowIntensity.charAt(0).toUpperCase() + flowIntensity.slice(1)
      : '';
    try {
      const response = await fetch(`${BASE_URL}/save_women_log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', lang },
        body: JSON.stringify({
          goqiiUserId: USER_ID,
          date,
          flow: formattedFlow,
          moods: selectedMoods,
          symptoms: selectedSymptoms
        })
      });
      await response.json();
      setTimeout(() => {
        setLoading(false);
        navigate(-1);
      }, 300);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save. Please try again.');
      setLoading(false);
    }
  };

  const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1).getDay();

  const renderMiniCalendar = () => {
    const parsed = new Date(currentDate);
    const daysInMonth = getDaysInMonth(parsed);
    const firstDay = getFirstDayOfMonth(parsed);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`e-${i}`} className="mini-calendar-day empty" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const menstruationDates = apiData?.cyclePhases?.menstruation || [];
      const fd = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isMens = menstruationDates.includes(fd);
      const isSel = day === selectedDate;
      const cls = ['mini-calendar-day', isSel && 'selected', isMens && 'menstruation'].filter(Boolean).join(' ');
      days.push(<div key={day} className={cls}>{day}</div>);
    }
    return days;
  };

  const formatDisplayDate = () => {
    const parsed = new Date(currentDate);
    return `${monthNames[parsed.getMonth()]} ${parsed.getFullYear()}`;
  };

  return (
    <div className="log-flow-mood-symptoms">
      {loading && (
        <div className="loading-overlay"><div className="spinner" /></div>
      )}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button className="error-close" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <header className="log-header">
        <h1 className="log-title">{t('womenLogs.title', 'Log Flow, Symptoms & Moods')}</h1>
      </header>

      {/* Mini Calendar */}
      <div className="mini-calendar-container">
        <div className="mini-calendar-month">{formatDisplayDate()}</div>
        <div className="mini-calendar-grid">
          {dayNames.map(d => <div key={d} className="mini-day-name">{d}</div>)}
          {renderMiniCalendar()}
        </div>
        <p className="calendar-info-text">
          {t('womenLogs.calendarNote', 'Logging flow will not add a period. To add or delete period, select a date from the calendar.')}
        </p>
      </div>

      {/* Flow Intensity */}
      <div className="selection-section">
        <div className="section-title">{t('womenLogs.flowIntensity', 'Flow Intensity')}</div>
        <div className="flow-intensity-grid">
          {apiData?.flowResult?.map(option => (
            <div
              key={option.id}
              className={`flow-option${flowIntensity === option.name.toLowerCase() ? ' selected' : ''}`}
              onClick={() => handleFlowIntensityClick(option.name.toLowerCase())}
            >
              <img src={option.image} alt={option.name} className="flow-icon" />
              <div className="flow-label">{tl('flowNames', option.name)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Symptoms */}
      <div className="selection-section">
        <div className="section-title">{t('womenLogs.symptoms', 'Symptoms')}</div>
        <div className="symptoms-grid">
          {apiData?.symptomResult?.map(option => (
            <div
              key={option.id}
              className={`symptom-option${selectedSymptoms.includes(option.name) ? ' selected' : ''}`}
              onClick={() => handleSymptomClick(option.name)}
            >
              <img src={option.image} alt={option.name} className="symptom-icon" />
              <div className="symptom-label">{tl('symptomNames', option.name)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Moods */}
      <div className="selection-section">
        <div className="section-title">{t('womenLogs.moods', 'Moods')}</div>
        <div className="moods-grid">
          {apiData?.moodResult?.map(option => (
            <div
              key={option.id}
              className={`mood-option${selectedMoods.includes(option.name) ? ' selected' : ''}`}
              onClick={() => handleMoodClick(option.name)}
            >
              <img src={option.image} alt={option.name} className="mood-icon" />
              <div className="mood-label">{tl('moodNames', option.name)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="save-section">
        <button className="save-btn" onClick={handleSave} disabled={loading}>
          {loading ? t('womenLogs.saving', 'Saving...') : t('womenLogs.save', 'SAVE')}
        </button>
      </div>
    </div>
  );
};

export default WomenLogs;
